import * as React from "react";

import H01, { H01Props } from "@stadium/shared/src/components/plasmic/H01";
import H02, { H02Props } from "@stadium/shared/src/components/plasmic/H02";
import H03, { H03Props } from "@stadium/shared/src/components/plasmic/H03";
import H04, { H04Props } from "@stadium/shared/src/components/plasmic/H04";
import H05, { H05Props } from "@stadium/shared/src/components/plasmic/H05";
import H06, { H06Props } from "@stadium/shared/src/components/plasmic/H06";
import H07, { H07Props } from "@stadium/shared/src/components/plasmic/H07";
import H08, { H08Props } from "@stadium/shared/src/components/plasmic/H08";

import {
  HeroVariant,
  H_01,
  H_02,
  H_03,
  H_04,
  H_05,
  H_06,
  H_07,
  H_08,
} from "@stadium/shared/src/helpers/plasmic/static-slices";
import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

// Individual Hero component props with prefixed names for Hero wrapper
export interface H01HeroProps {
  h01EyebrowText?: H01Props["eyebrowText"];
  h01Heading?: H01Props["heading"];
  h01Description?: H01Props["description"];
  h01CtaText?: H01Props["ctaText"];
  h01CtaLinkType?: H01Props["ctaLinkType"];
  h01CtaOpenInNewTab?: H01Props["openInNewTab"];
  h01CtaOtherUrl?: H01Props["otherUrl"];
  h01CtaCategorySlug?: H01Props["categorySlug"];
  h01Cta2Text?: H01Props["cta2Text"];
  h01Cta2LinkType?: H01Props["cta2LinkType"];
  h01Cta2OpenInNewTab?: H01Props["cta2OpenInNewTab"];
  h01Cta2OtherUrl?: H01Props["cta2OtherUrl"];
  h01Cta2CategorySlug?: H01Props["cta2CategorySlug"];
  h01MobileImg?: H01Props["mobileImg"];
  h01TabletImg?: H01Props["tabletImg"];
  h01DesktopImg?: H01Props["desktopImg"];
  h01HighResImg?: H01Props["highResImg"];
  h01ImageAlt?: H01Props["imageAlt"];
  h01StandardMargin?: H01Props["standardMargin"];
}

export interface H02HeroProps {
  h02Heading?: H02Props["heading"];
  h02Description?: H02Props["description"];
  h02PrimaryButtonText?: H02Props["primaryButtonText"];
  h02PrimaryButtonUrl?: H02Props["primaryButtonUrl"];
  h02SecondaryButtonText?: H02Props["secondaryButtonText"];
  h02SecondaryButtonUrl?: H02Props["secondaryButtonUrl"];
  h02HeroImage?: H02Props["heroImage"];
  h02HeroImageAlt?: H02Props["heroImageAlt"];
}

export interface H03HeroProps {
  h03Heading?: H03Props["heading"];
  h03Description?: H03Props["description"];
  h03TeamTitle?: H03Props["teamTitle"];
  h03TeamDate?: H03Props["teamDate"];
  h03TeamDateFormat?: H03Props["teamDateFormat"];
  h03BadgeLogo?: H03Props["badgeLogo"];
  h03BadgeLogoAlt?: H03Props["badgeLogoAlt"];
  h03HeroImage?: H03Props["heroImage"];
  h03HeroImageAlt?: H03Props["heroImageAlt"];
}

export interface H04HeroProps {
  h04Heading?: H04Props["heading"];
  h04Eyebrow?: H04Props["eyebrow"];
  h04Description?: H04Props["description"];
  h04PrimaryButtonText?: H04Props["primaryButtonText"];
  h04PrimaryButtonLinkType?: H04Props["primaryButtonLinkType"];
  h04PrimaryButtonOpenInNewTab?: H04Props["primaryButtonOpenInNewTab"];
  h04PrimaryButtonOtherUrl?: H04Props["primaryButtonOtherUrl"];
  h04PrimaryButtonCategorySlug?: H04Props["primaryButtonCategorySlug"];
  h04Image?: H04Props["image"];
  h04ImageAlt?: H04Props["imageAlt"];
}

export interface H05HeroProps {
  h05Heading?: H05Props["heading"];
  h05Description?: H05Props["description"];
  h05BulletPoints?: H05Props["bulletPoints"];
  h05PrimaryButtonText?: H05Props["primaryButtonText"];
  h05PrimaryButtonUrl?: H05Props["primaryButtonUrl"];
  h05SecondaryButtonText?: H05Props["secondaryButtonText"];
  h05SecondaryButtonUrl?: H05Props["secondaryButtonUrl"];
  h05HeroImage?: H05Props["heroImage"];
  h05HeroImageAlt?: H05Props["heroImageAlt"];
}

export interface H06HeroProps {
  h06Heading?: H06Props["heading"];
  h06Description?: H06Props["description"];
  h06PrimaryButtonText?: H06Props["primaryButtonText"];
  h06PrimaryButtonUrl?: H06Props["primaryButtonUrl"];
  h06SecondaryButtonText?: H06Props["secondaryButtonText"];
  h06SecondaryButtonUrl?: H06Props["secondaryButtonUrl"];
  h06HeroImage?: H06Props["heroImage"];
  h06HeroImageAlt?: H06Props["heroImageAlt"];
}

export interface H07HeroProps {
  h07PrimaryButtonWidth?: H07Props["primaryButtonWidth"];
  h07SecondaryButtonWidth?: H07Props["secondaryButtonWidth"];
  h07Heading?: H07Props["heading"];
  h07Description?: H07Props["description"];
  h07PrimaryButtonText?: H07Props["primaryButtonText"];
  h07PrimaryButtonLinkType?: H07Props["primaryButtonLinkType"];
  h07PrimaryButtonOpenInNewTab?: H07Props["primaryButtonOpenInNewTab"];
  h07PrimaryButtonOtherUrl?: H07Props["primaryButtonOtherUrl"];
  h07PrimaryButtonCategorySlug?: H07Props["primaryButtonCategorySlug"];
  h07SecondaryButtonText?: H07Props["secondaryButtonText"];
  h07SecondaryButtonLinkType?: H07Props["secondaryButtonLinkType"];
  h07SecondaryButtonOpenInNewTab?: H07Props["secondaryButtonOpenInNewTab"];
  h07SecondaryButtonOtherUrl?: H07Props["secondaryButtonOtherUrl"];
  h07SecondaryButtonCategorySlug?: H07Props["secondaryButtonCategorySlug"];
  h07HeroImage?: H07Props["heroImage"];
  h07HeroImageAlt?: H07Props["heroImageAlt"];
  h07ShowReviewBadge?: H07Props["showReviewBadge"];
  h07ReviewBadgeLogo?: H07Props["reviewBadgeLogo"];
  h07ReviewBadgeLogoAlt?: H07Props["reviewBadgeLogoAlt"];
  h07ReviewRating?: H07Props["reviewRating"];
  h07ReviewCount?: H07Props["reviewCount"];
  h07ReviewStarIcon?: H07Props["reviewStarIcon"];
  h07ShowLogoStrip?: H07Props["showLogoStrip"];
  h07LogoStripEyebrow?: H07Props["logoStripEyebrow"];
  h07LogoStripHeading?: H07Props["logoStripHeading"];
  h07CompanyLogo1?: H07Props["companyLogo1"];
  h07CompanyLogo1Alt?: H07Props["companyLogo1Alt"];
  h07CompanyLogo2?: H07Props["companyLogo2"];
  h07CompanyLogo2Alt?: H07Props["companyLogo2Alt"];
  h07CompanyLogo3?: H07Props["companyLogo3"];
  h07CompanyLogo3Alt?: H07Props["companyLogo3Alt"];
}

export interface H08HeroProps {
  heading?: H08Props["heading"];
  description?: H08Props["description"];
  summaryDescription?: H08Props["summaryDescription"];
  teamTitle?: H08Props["teamTitle"];
  teamDate?: H08Props["teamDate"];
  teamDateFormat?: H08Props["teamDateFormat"];
  testimonialHeading?: H08Props["testimonialHeading"];
  badgeLogo?: H08Props["badgeLogo"];
  heroImage?: H08Props["image"];
  testimonialAuthorName?: string;
  testimonialAuthorImage?: { image: string; alt: string };
  testimonialAuthorCompanyLogo?: { image: string; alt: string };
}

export interface HeroProps
  extends H01HeroProps,
    H02HeroProps,
    H03HeroProps,
    H04HeroProps,
    H05HeroProps,
    H06HeroProps,
    H07HeroProps,
    H08HeroProps {
  variant?: HeroVariant;
  id?: string;
  className?: string;
  categorySlug?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}
const Hero: React.FC<Readonly<HeroProps>> = (props) => {
  const { variant = H_01, categorySlug, linkRouteDefaults } = props;

  switch (variant) {
    case H_01: {
      const {
        h01EyebrowText,
        h01Heading,
        h01Description,
        h01CtaText,
        h01CtaLinkType,
        h01CtaOpenInNewTab,
        h01CtaOtherUrl,
        h01CtaCategorySlug,
        h01Cta2Text,
        h01Cta2LinkType,
        h01Cta2OpenInNewTab,
        h01Cta2OtherUrl,
        h01Cta2CategorySlug,
        h01MobileImg,
        h01TabletImg,
        h01DesktopImg,
        h01HighResImg,
        h01ImageAlt,
        h01StandardMargin,
        className,
      } = props;

      return (
        <H01
          eyebrowText={h01EyebrowText}
          heading={h01Heading}
          description={h01Description}
          ctaText={h01CtaText}
          ctaLinkType={h01CtaLinkType}
          openInNewTab={h01CtaOpenInNewTab}
          otherUrl={h01CtaOtherUrl}
          categorySlug={h01CtaCategorySlug || categorySlug}
          cta2Text={h01Cta2Text}
          cta2LinkType={h01Cta2LinkType}
          cta2OpenInNewTab={h01Cta2OpenInNewTab}
          cta2OtherUrl={h01Cta2OtherUrl}
          cta2CategorySlug={h01Cta2CategorySlug || categorySlug}
          mobileImg={h01MobileImg}
          tabletImg={h01TabletImg}
          desktopImg={h01DesktopImg}
          highResImg={h01HighResImg}
          imageAlt={h01ImageAlt}
          standardMargin={h01StandardMargin}
          className={className}
          linkRouteDefaults={linkRouteDefaults}
        />
      );
    }

    case H_02: {
      const {
        h02Heading,
        h02Description,
        h02PrimaryButtonText,
        h02PrimaryButtonUrl,
        h02SecondaryButtonText,
        h02SecondaryButtonUrl,
        h02HeroImage,
        h02HeroImageAlt,
        className,
      } = props;

      return (
        <H02
          heading={h02Heading}
          description={h02Description}
          primaryButtonText={h02PrimaryButtonText}
          primaryButtonUrl={h02PrimaryButtonUrl}
          secondaryButtonText={h02SecondaryButtonText}
          secondaryButtonUrl={h02SecondaryButtonUrl}
          heroImage={h02HeroImage}
          heroImageAlt={h02HeroImageAlt}
          className={className}
        />
      );
    }

    case H_03: {
      const {
        h03Heading,
        h03Description,
        h03TeamTitle,
        h03TeamDate,
        h03TeamDateFormat,
        h03BadgeLogo,
        h03BadgeLogoAlt,
        h03HeroImage,
        h03HeroImageAlt,
        className,
      } = props;

      return (
        <H03
          heading={h03Heading}
          description={h03Description}
          teamTitle={h03TeamTitle}
          teamDate={h03TeamDate}
          teamDateFormat={h03TeamDateFormat}
          badgeLogo={h03BadgeLogo}
          badgeLogoAlt={h03BadgeLogoAlt}
          heroImage={h03HeroImage}
          heroImageAlt={h03HeroImageAlt}
          className={className}
        />
      );
    }

    case H_04: {
      const {
        h04Heading,
        h04Eyebrow,
        h04Description,
        h04PrimaryButtonText,
        h04PrimaryButtonLinkType,
        h04PrimaryButtonOpenInNewTab,
        h04PrimaryButtonOtherUrl,
        h04PrimaryButtonCategorySlug,
        h04Image,
        h04ImageAlt,
        className,
      } = props;

      return (
        <H04
          heading={h04Heading}
          eyebrow={h04Eyebrow}
          description={h04Description}
          primaryButtonText={h04PrimaryButtonText}
          primaryButtonLinkType={h04PrimaryButtonLinkType}
          primaryButtonOpenInNewTab={h04PrimaryButtonOpenInNewTab}
          primaryButtonOtherUrl={h04PrimaryButtonOtherUrl}
          primaryButtonCategorySlug={h04PrimaryButtonCategorySlug || categorySlug}
          image={h04Image}
          imageAlt={h04ImageAlt}
          className={className}
        />
      );
    }

    case H_05: {
      const {
        h05Heading,
        h05Description,
        h05BulletPoints,
        h05PrimaryButtonText,
        h05PrimaryButtonUrl,
        h05SecondaryButtonText,
        h05SecondaryButtonUrl,
        h05HeroImage,
        h05HeroImageAlt,
        className,
      } = props;

      return (
        <H05
          heading={h05Heading}
          description={h05Description}
          bulletPoints={h05BulletPoints}
          primaryButtonText={h05PrimaryButtonText}
          primaryButtonUrl={h05PrimaryButtonUrl}
          secondaryButtonText={h05SecondaryButtonText}
          secondaryButtonUrl={h05SecondaryButtonUrl}
          heroImage={h05HeroImage}
          heroImageAlt={h05HeroImageAlt}
          className={className}
        />
      );
    }

    case H_06: {
      const {
        h06Heading,
        h06Description,
        h06PrimaryButtonText,
        h06PrimaryButtonUrl,
        h06SecondaryButtonText,
        h06SecondaryButtonUrl,
        h06HeroImage,
        h06HeroImageAlt,
        className,
      } = props;

      return (
        <H06
          heading={h06Heading}
          description={h06Description}
          primaryButtonText={h06PrimaryButtonText}
          primaryButtonUrl={h06PrimaryButtonUrl}
          secondaryButtonText={h06SecondaryButtonText}
          secondaryButtonUrl={h06SecondaryButtonUrl}
          heroImage={h06HeroImage}
          heroImageAlt={h06HeroImageAlt}
          className={className}
        />
      );
    }

    case H_07: {
      const {
        h07PrimaryButtonWidth,
        h07SecondaryButtonWidth,
        h07Heading,
        h07Description,
        h07PrimaryButtonText,
        h07PrimaryButtonLinkType,
        h07PrimaryButtonOpenInNewTab,
        h07PrimaryButtonOtherUrl,
        h07PrimaryButtonCategorySlug,
        h07SecondaryButtonText,
        h07SecondaryButtonLinkType,
        h07SecondaryButtonOpenInNewTab,
        h07SecondaryButtonOtherUrl,
        h07SecondaryButtonCategorySlug,
        h07HeroImage,
        h07HeroImageAlt,
        h07ShowReviewBadge,
        h07ReviewBadgeLogo,
        h07ReviewBadgeLogoAlt,
        h07ReviewRating,
        h07ReviewCount,
        h07ReviewStarIcon,
        h07ShowLogoStrip,
        h07LogoStripEyebrow,
        h07LogoStripHeading,
        h07CompanyLogo1,
        h07CompanyLogo1Alt,
        h07CompanyLogo2,
        h07CompanyLogo2Alt,
        h07CompanyLogo3,
        h07CompanyLogo3Alt,
        className,
      } = props;

      return (
        <H07
          primaryButtonWidth={h07PrimaryButtonWidth}
          secondaryButtonWidth={h07SecondaryButtonWidth}
          heading={h07Heading}
          description={h07Description}
          primaryButtonText={h07PrimaryButtonText}
          primaryButtonLinkType={h07PrimaryButtonLinkType}
          primaryButtonOpenInNewTab={h07PrimaryButtonOpenInNewTab}
          primaryButtonOtherUrl={h07PrimaryButtonOtherUrl}
          primaryButtonCategorySlug={h07PrimaryButtonCategorySlug || categorySlug}
          secondaryButtonText={h07SecondaryButtonText}
          secondaryButtonLinkType={h07SecondaryButtonLinkType}
          secondaryButtonOpenInNewTab={h07SecondaryButtonOpenInNewTab}
          secondaryButtonOtherUrl={h07SecondaryButtonOtherUrl}
          secondaryButtonCategorySlug={h07SecondaryButtonCategorySlug || categorySlug}
          heroImage={h07HeroImage}
          heroImageAlt={h07HeroImageAlt}
          showReviewBadge={h07ShowReviewBadge}
          reviewBadgeLogo={h07ReviewBadgeLogo}
          reviewBadgeLogoAlt={h07ReviewBadgeLogoAlt}
          reviewRating={h07ReviewRating}
          reviewCount={h07ReviewCount}
          reviewStarIcon={h07ReviewStarIcon}
          showLogoStrip={h07ShowLogoStrip}
          logoStripEyebrow={h07LogoStripEyebrow}
          logoStripHeading={h07LogoStripHeading}
          companyLogo1={h07CompanyLogo1}
          companyLogo1Alt={h07CompanyLogo1Alt}
          companyLogo2={h07CompanyLogo2}
          companyLogo2Alt={h07CompanyLogo2Alt}
          companyLogo3={h07CompanyLogo3}
          companyLogo3Alt={h07CompanyLogo3Alt}
          className={className}
          linkRouteDefaults={linkRouteDefaults}
        />
      );
    }

    case H_08: {
      const {
        heading,
        description,
        summaryDescription,
        teamTitle,
        teamDate,
        teamDateFormat,
        testimonialHeading,
        badgeLogo,
        heroImage,
        testimonialAuthorName,
        testimonialAuthorImage,
        testimonialAuthorCompanyLogo,
        className,
      } = props;

      return (
        <H08
          heading={heading}
          description={description}
          summaryDescription={summaryDescription}
          teamTitle={teamTitle}
          teamDate={teamDate}
          teamDateFormat={teamDateFormat}
          testimonialHeading={testimonialHeading}
          badgeLogo={badgeLogo}
          image={heroImage}
          testimonial={{
            testimonialAuthorName: testimonialAuthorName ?? "",
            testimonialAuthorImage: testimonialAuthorImage ?? { image: "", alt: "" },
            testimonialAuthorCompanyLogo: testimonialAuthorCompanyLogo,
          }}
          className={className}
        />
      );
    }

    default:
      return null;
  }
};

export default Hero;
